import { t } from '@lingui/core/macro'
import { lazy } from 'react'
import { matchPath } from 'react-router'
import { Roles } from '@/core/enums/roles'
import { TipoUsuario } from '@/core/enums/tipo-usuario'
import { PASTA_QUERY } from '@/modules/biblioteca-virtual/data/pasta-query'
import BibliotecaVirtual from '@/modules/biblioteca-virtual/ui/biblioteca-virtual'
import { ImportarUsuarios } from '@/modules/importar-usuarios/ui/importar-usuarios'
import { SuperAdmin } from '@/modules/super-admin/ui/super-admin'
import { getLabel } from '@/core/utils/global-labels.js'
import { HomeDesktop } from '@/modules/home/ui/home-desktop'
import UsuarioEdicaoWizard from '@/modules/usuarios-edicao/ui/usuario-edicao-wizard'

const Auditoria = lazy(() => import('@/modules/auditoria/ui/auditoria'))
const MinhaContaContainer = lazy(
  () => import('@/modules/minha-conta/ui/minha-conta')
)
const DealerListagem = lazy(
  () => import('@/modules/dealer-listagem/ui/dealer-listagem')
)
const DealerEdicaoContainer = lazy(
  () => import('@/modules/dealer-edicao/ui/dealer-wizard')
)
const GrupoListagemContainer = lazy(
  () => import('@/modules/grupo-listagem/ui/grupo-dealers-listagem')
)
const GrupoUsuariosListagemContainerCortex = lazy(
  () => import('@/modules/grupo-usuarios-listagem/ui/grupo-usuarios-listagem')
)
const GrupoUsuariosEdicaoContainer = lazy(
  () => import('@/modules/grupo-usuarios-edicao/ui/grupo-usuarios-edicao')
)
const UsuarioListagemContainer = lazy(
  () => import('@/modules/usuarios-listagem/ui/usuario-listagem')
)

const AusenciaTemporariaListagem = lazy(
  () => import('@/modules/ausencia-temporaria/ui/ausencia-temporaria')
)

const ComunicadoListagem = lazy(
  () => import('@/modules/comunicado-listagem/ui/comunicado-listagem-desktop')
)
const ComunicadoDashboardContainer = lazy(
  () => import('@/modules/comunicado-dashboard/ui/comunicado-dashboard')
)
const ComunicadoVisualizacaoContainer = lazy(
  () => import('@/modules/comunicado-visualizacao/ui/comunicado-visualizacao')
)
const ComunicadoEnvioVisualizacao = lazy(
  () => import('@/modules/comunicado-visualizacao/ui/comunicado-envio')
)
const ComunicadoEdicaoContainer = lazy(
  () => import('@/modules/comunicado-edicao/ui/comunicado-edicao')
)
const SlideListagem = lazy(
  () => import('@/modules/slides-listagem/ui/slide-listagem')
)

const SlideEdicaoContainer = lazy(
  () => import('@/modules/slides-edicao/ui/slide-edicao')
)

const AnuncioListagem = lazy(
  () => import('@/modules/anuncio-listagem/ui/anuncio-listagem')
)
const AnuncioEdicaoContainer = lazy(
  () => import('@/modules/anuncio-edicao/ui/anuncio-edicao')
)
const NotificacaoListagemContainer = lazy(
  () => import('@/modules/notificacoes/ui/notificacao-listagem')
)
const NotificacaoGenericaListagemContainer = lazy(
  () =>
    import(
      '@/modules/notificacoes-genericas-listagem/ui/notificacao-generica-listagem'
    )
)
const NotificacaoGenericaEdicaoContainer = lazy(
  () =>
    import(
      '@/modules/notificacoes-genericas-edicao/ui/notificacao-generica-edicao'
    )
)
const GrupoEdicaoContainer = lazy(
  () => import('@/modules/grupo-edicao/ui/grupo-edicao')
)
const RelatorioPage = lazy(() => import('@/modules/relatorios/ui/index'))

export const Routes = {
  ROOT: { route: '/', label: 'Portal' },
  HOME: {
    route: '/home',
    label: () => t`Página Inicial`,
    title: t`Página Inicial`,
    component: HomeDesktop,
    menu: true
  },
  SUPER_ADMIN: {
    route: '/superAdmin',
    label: () => t`Página Inicial`,
    title: t`Página Inicial`,
    component: SuperAdmin,
    tiposUsuario: [TipoUsuario.TECSINAPSE],
    menu: true
  },
  GRUPOS: {
    route: '/grupos',
    label: () => getLabel('label.grupos'),
    title: () => getLabel('label.grupos'),
    component: GrupoListagemContainer,
    roles: [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO],
    menu: true
  },
  GRUPO_NOVO: {
    route: '/grupos/novo',
    label: () => getLabel('novo.grupo'),
    title: () => getLabel('novo.grupo'),
    component: GrupoEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
  },
  GRUPO: {
    route: '/grupos/:id',
    label: () => t`Editar`,
    title: () => getLabel('label.grupos'),
    component: GrupoEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
  },
  DEALER_NOVO: {
    route: '/dealers/novo',
    label: () => getLabel('novo.dealer'),
    title: () => getLabel('novo.dealer'),
    component: DealerEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
  },
  DEALER: {
    route: '/dealers/:id',
    label: () => t`Editar`,
    title: () => getLabel('label.dealer'),
    component: DealerEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO]
  },
  DEALERS: {
    route: '/dealers',
    label: () => getLabel('label.dealers'),
    title: () => getLabel('label.dealers'),
    component: DealerListagem,
    roles: [Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO],
    menu: true
  },
  USUARIOS: {
    route: '/usuarios',
    label: () => t`Usuários`,
    title: t`Usuários`,
    component: UsuarioListagemContainer,
    roles: [
      Roles.PORTAL_MANUTENCAO_USUARIOS,
      Roles.PORTAL_CONSULTA_VISUALIZACAO_USUARIOS
    ],
    menu: true
  },
  USUARIO_NOVO: {
    route: '/usuarios/novo',
    label: () => t`Novo Usuário`,
    title: t`Novo Usuário`,
    component: UsuarioEdicaoWizard,
    roles: [Roles.PORTAL_MANUTENCAO_USUARIOS]
  },
  USUARIO: {
    route: '/usuarios/:id',
    label: () => t`Editar`,
    title: t`Usuário`,
    component: UsuarioEdicaoWizard,
    roles: [Roles.PORTAL_MANUTENCAO_USUARIOS]
  },
  AUSENCIA_TEMPORARIA: {
    route: '/usuarios/:id/ausencia-temporaria',
    label: t`Ausências Temporárias`,
    title: t`Ausências Temporárias`,
    component: AusenciaTemporariaListagem,
    roles: [Roles.PORTAL_AUSENCIA_TEMPORARIA],
    menu: true
  },
  GRUPOS_USUARIOS: {
    route: '/gruposUsuarios',
    label: () => t`Grupos de Usuários`,
    title: t`Grupos de Usuários`,
    component: GrupoUsuariosListagemContainerCortex,
    roles: [Roles.PORTAL_MANUTENCAO_GRUPOS_USUARIOS],
    menu: true
  },
  GRUPO_USUARIOS_NOVO: {
    route: '/gruposUsuarios/novo',
    label: () => t`Novo Grupo de Usuários`,
    title: t`Novo Grupo de Usuários`,
    component: GrupoUsuariosEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_GRUPOS_USUARIOS]
  },
  GRUPO_USUARIOS: {
    route: '/gruposUsuarios/:id',
    label: () => t`Editar`,
    title: t`Grupos de Usuários`,
    component: GrupoUsuariosEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_GRUPOS_USUARIOS]
  },
  AUDITORIA: {
    route: '/auditoria/:id/:tipo',
    label: () => t`Auditoria`,
    title: t`Auditoria`,
    component: Auditoria,
    roles: [
      Roles.PORTAL_MANUTENCAO_USUARIOS,
      Roles.PORTAL_MANUTENCAO_DEALERS_GRUPO,
      Roles.PORTAL_MANUTENCAO_GRUPOS_USUARIOS,
      Roles.PORTAL_MANUTENCAO_SLIDES,
      Roles.PORTAL_MANUTENCAO_ANUNCIOS
    ]
  },
  BIBLIOTECA_VIRTUAL_PASTA: {
    route: '/biblioteca/:idPasta',
    label: async ({ idPasta }, { apolloClient }) => {
      const {
        data: { pasta }
      } = await apolloClient.query({
        query: PASTA_QUERY,
        variables: {
          idPasta
        }
      })

      return pasta.nome
    },
    title: t`Biblioteca Virtual`,
    component: BibliotecaVirtual
  },
  BIBLIOTECA_VIRTUAL: {
    route: '/biblioteca',
    label: () => t`Biblioteca Virtual`,
    title: t`Biblioteca Virtual`,
    component: BibliotecaVirtual,
    menu: true
  },
  COMUNICADOS_NOVO: {
    route: '/comunicados/novo',
    label: () => t`Novo Comunicado`,
    title: t`Novo Comunicado`,
    component: ComunicadoEdicaoContainer,
    roles: [
      Roles.PORTAL_CRIACAO_COMUNICADOS,
      Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
    ]
  },
  COMUNICADO_EDICAO: {
    route: '/comunicados/editar/:id',
    label: () => t`Editar`,
    title: t`Comunicado`,
    component: ComunicadoEdicaoContainer,
    roles: [
      Roles.PORTAL_CRIACAO_COMUNICADOS,
      Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
    ]
  },
  COMUNICADO_ERRATA: {
    route: '/comunicados/errata/:id',
    label: () => t`Errata`,
    title: t`Comunicado`,
    component: ComunicadoEdicaoContainer,
    roles: [
      Roles.PORTAL_CRIACAO_COMUNICADOS,
      Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
    ]
  },
  COMUNICADO_ENVIO_VISUALIZACAO: {
    route: '/comunicados/visualizarEnvio/:id',
    label: () => t`Visualizar`,
    title: t`Comunicado`,
    component: ComunicadoEnvioVisualizacao
  },
  COMUNICADO_VISUALIZACAO: {
    route: '/comunicados/visualizar/:id',
    label: () => t`Visualizar`,
    title: t`Comunicado`,
    component: ComunicadoVisualizacaoContainer,
    roles: [
      Roles.PORTAL_CRIACAO_COMUNICADOS,
      Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
    ]
  },
  COMUNICADO_DASHBOARD: {
    route: '/comunicados/dashboard/:id',
    label: () => t`Editar`,
    title: t`Comunicado`,
    component: ComunicadoDashboardContainer,
    roles: [
      Roles.PORTAL_CRIACAO_COMUNICADOS,
      Roles.PORTAL_APROVACAO_ENVIO_COMUNICADOS
    ]
  },
  COMUNICADOS: {
    route: '/comunicados',
    label: () => t`Comunicados`,
    title: t`Comunicados`,
    component: ComunicadoListagem,
    menu: true
  },
  MINHA_CONTA: {
    title: t`Minha Conta`,
    route: '/minha-conta',
    label: () => t`Minha Conta`,
    component: MinhaContaContainer
  },
  SLIDES: {
    route: '/slides',
    label: () => t`Slides`,
    title: t`Slides`,
    component: SlideListagem,
    roles: [Roles.PORTAL_MANUTENCAO_SLIDES],
    menu: true
  },
  SLIDE_NOVO: {
    route: '/slides/novo',
    label: () => t`Novo Slide`,
    title: t`Novo Slide`,
    component: SlideEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_SLIDES]
  },
  SLIDE_EDICAO: {
    route: '/slides/editar/:id',
    label: () => t`Editar`,
    title: t`Slides`,
    component: SlideEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_SLIDES]
  },
  ANUNCIO_NOVO: {
    route: '/anuncios/novo',
    label: () => t`Novo Anúncio`,
    title: t`Novo Anúncio`,
    component: AnuncioEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_ANUNCIOS]
  },
  ANUNCIO_EDICAO: {
    route: '/anuncios/editar/:id',
    label: () => t`Editar`,
    title: t`Anúncios`,
    component: AnuncioEdicaoContainer,
    roles: [Roles.PORTAL_MANUTENCAO_ANUNCIOS]
  },
  ANUNCIOS: {
    route: '/anuncios',
    label: () => t`Anúncios`,
    title: t`Anúncios`,
    component: AnuncioListagem,
    roles: [Roles.PORTAL_MANUTENCAO_ANUNCIOS],
    menu: true
  },
  NOTIFICACOES: {
    route: '/notificacoes',
    label: () => t`Notificações`,
    title: t`Notificações`,
    component: NotificacaoListagemContainer
  },

  NOTIFICACOES_GENERICAS: {
    route: '/notificacoesGenericas',
    label: () => t`Notificações`,
    title: t`Notificações`,
    component: NotificacaoGenericaListagemContainer,
    roles: [Roles.PORTAL_NOTIFICACOES_GENERICAS],
    menu: true
  },
  NOTIFICACAO_NOVA: {
    route: '/notificacoesGenericas/nova/:tipo',
    label: ({ tipo }) =>
      tipo === 'modelo' ? t`Novo Modelo` : t`Nova Notificação`,
    title: t`Notificações`,
    component: NotificacaoGenericaEdicaoContainer,
    roles: [Roles.PORTAL_NOTIFICACOES_GENERICAS]
  },
  NOTIFICACAO_EDICAO: {
    route: '/notificacoesGenericas/editar/:tipo/:id',
    label: ({ tipo, id }) => {
      switch (tipo) {
        case 'modelo':
          return t`Modelo #${id}`
        case 'comModelo':
          return t`Nova Notificação`
        default:
          return t`Editar`
      }
    },
    title: t`Notificações`,
    component: NotificacaoGenericaEdicaoContainer,
    roles: [Roles.PORTAL_NOTIFICACOES_GENERICAS]
  },
  IMPORTAR_USUARIOS: {
    route: '/importarUsuarios',
    label: () => t`Importar usuários`,
    title: t`Importar usuários`,
    component: ImportarUsuarios,
    roles: [Roles.PORTAL_IMPORTACAO_USUARIOS],
    menu: true
  },
  RELATORIO_PAGE: {
    route: '/relatorio/:slug',
    label: t`Relatório`,
    title: t`Relatório`,
    component: RelatorioPage,
    menu: true
  }
}

export const publicRoutes = [
  '/esqueci-senha',
  '/forgot-password',
  '/olvide-contrasena'
]

export function formatRouteForPush({ route }, params) {
  const a = params
  let routeEdited = route

  do {
    const param = a.shift() || ''

    routeEdited = routeEdited.replace(/:[^/]*/, param)
  } while (a.length)

  return routeEdited
}

export const getRouteByPath = path => {
  const filtered = Object.keys(Routes).filter(
    elem =>
      matchPath(path, Routes[elem].route) !== null &&
      Routes[elem].route !== Routes.ROOT.route
  )
  const [first] = filtered

  return Routes[first]
}

export const GetLocationTitle = path => {
  const route = getRouteByPath(path)

  if (route && route.title) {
    return route.title
  }

  return ''
}
